// AXIS ZERO - Game Over / Death Screen
// Clean minimal: large time, best+rank, three neon outline buttons

import React, { useState, useEffect, useMemo } from 'react';
import { RotateCcw, Share2, ArrowLeft, Copy, Link } from 'lucide-react';
import { DEATH_MESSAGES, GAME_STATES, shareCardGenerator, audioManager } from '../../game';
import { toast } from 'sonner';

export const GameOverOverlay = ({ 
  gameState, 
  score, 
  highScore, 
  isNewHighScore, 
  onRestart,
  onBack,
  gameOverData,
  leaderboardResult,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showShareFallback, setShowShareFallback] = useState(false);

  const deathMessage = useMemo(() => {
    return DEATH_MESSAGES[Math.floor(Math.random() * DEATH_MESSAGES.length)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  const formatTime = (time) => {
    if (time === null || time === undefined) return '\u2014';
    return time.toFixed(2);
  };

  useEffect(() => {
    if (gameState === GAME_STATES.GAME_OVER) {
      setIsVisible(true);
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setShowContent(false);
      setShowShareFallback(false);
    }
  }, [gameState]);

  if (!isVisible) return null;

  const maxStreak = gameOverData?.maxStreak || 0;
  const playerRank = leaderboardResult?.personalBestRank || null;
  const personalBestScore = leaderboardResult?.personalBestScore || null;
  
  const mainColor = '#00E5FF';
  const glowColor = 'rgba(0, 229, 255, 0.8)';

  const handleRestart = () => {
    audioManager.playRestart();
    onRestart();
  };

  const handleMenu = () => {
    audioManager.playButtonClick();
    onBack();
  };

  const handleShare = async () => {
    audioManager.playButtonClick();
    const result = await shareCardGenerator.shareNative(score, maxStreak);
    if (!result.success) {
      setShowShareFallback(true);
    } else {
      toast.success('Shared!');
    }
  };

  const handleCopyLink = () => {
    shareCardGenerator.copyLink();
    toast.success('Link copied!');
  };

  const handleCopyText = () => {
    shareCardGenerator.copyCaption(score, maxStreak);
    toast.success('Text copied!');
  };

  // Shared button style
  const btnStyle = {
    borderColor: mainColor,
    color: mainColor,
    backgroundColor: 'transparent',
    fontFamily: '"Rajdhani", sans-serif',
  };

  const btnHoverIn = (e) => {
    e.currentTarget.style.backgroundColor = mainColor;
    e.currentTarget.style.color = '#000';
  };

  const btnHoverOut = (e) => {
    e.currentTarget.style.backgroundColor = 'transparent';
    e.currentTarget.style.color = mainColor;
  };

  return (
    <div 
      data-testid="game-over-overlay"
      className={`absolute inset-0 z-50 flex flex-col items-center justify-center p-4 transition-opacity duration-500 ${showContent ? 'opacity-100' : 'opacity-0'}`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
    >
      <div className="flex flex-col items-center w-full max-w-md">
        {/* Death message - subtle */}
        <div 
          data-testid="death-message"
          className="text-sm uppercase tracking-[0.3em] mb-3"
          style={{ 
            color: 'rgba(255,255,255,0.25)',
            fontFamily: '"Rajdhani", sans-serif',
          }}
        >
          {deathMessage}
        </div>

        {/* Large final time */}
        <div 
          data-testid="final-score"
          className="text-5xl sm:text-6xl md:text-7xl font-black tracking-wider mb-1"
          style={{ 
            color: isNewHighScore ? '#FF1744' : mainColor,
            textShadow: isNewHighScore 
              ? '0 0 20px rgba(255,23,68,0.8), 0 0 40px rgba(255,23,68,0.4)'
              : `0 0 20px ${glowColor}, 0 0 40px ${glowColor}`,
            fontFamily: '"Orbitron", sans-serif',
          }}
        >
          {formatTime(score)}
        </div>
        <div 
          className="text-xs uppercase tracking-[0.2em] mb-5"
          style={{ color: 'rgba(255,255,255,0.25)', fontFamily: '"Rajdhani", sans-serif' }}
        >
          seconds
        </div>

        {/* BEST + RANK + STREAK - clean text row */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mb-6 text-sm uppercase tracking-widest" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
          {isNewHighScore ? (
            <span data-testid="new-record-badge" style={{ color: '#FF1744' }}>NEW RECORD!</span>
          ) : personalBestScore !== null ? (
            <span data-testid="personal-best-display" style={{ color: 'rgba(255,255,255,0.4)' }}>
              BEST {(personalBestScore / 100).toFixed(2)}s
            </span>
          ) : null}
          {playerRank && (
            <span data-testid="player-rank" style={{ color: mainColor }}>RANK #{playerRank}</span>
          )}
          {maxStreak > 0 && (
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>x{maxStreak} STREAK</span>
          )}
        </div>

        {/* Share fallback (shown when Web Share API not available) */}
        {showShareFallback && (
          <div className="flex gap-2 mb-4">
            <button
              data-testid="copy-link-btn"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-4 py-2 border text-xs uppercase tracking-wider transition-opacity opacity-70 hover:opacity-100"
              style={{ borderColor: mainColor, color: mainColor }}
            >
              <Link size={12} />
              Copy Link
            </button>
            <button
              data-testid="copy-text-btn"
              onClick={handleCopyText}
              className="flex items-center gap-1.5 px-4 py-2 border text-xs uppercase tracking-wider transition-opacity opacity-70 hover:opacity-100"
              style={{ borderColor: mainColor, color: mainColor }}
            >
              <Copy size={12} />
              Copy Text
            </button>
          </div>
        )}

        {/* Three buttons in one row */}
        <div 
          data-testid="gameover-buttons"
          className="flex gap-3"
        >
          <button
            data-testid="restart-button"
            onClick={handleRestart}
            className="px-5 py-3 border-2 font-bold uppercase tracking-widest transition-all duration-200 flex items-center gap-2"
            style={btnStyle}
            onMouseEnter={btnHoverIn}
            onMouseLeave={btnHoverOut}
          >
            <RotateCcw size={16} />
            RESTART
          </button>
          
          <button
            data-testid="share-button"
            onClick={handleShare}
            className="px-5 py-3 border-2 font-bold uppercase tracking-widest transition-all duration-200 flex items-center gap-2"
            style={btnStyle}
            onMouseEnter={btnHoverIn}
            onMouseLeave={btnHoverOut}
          >
            <Share2 size={16} />
            SHARE
          </button>

          <button
            data-testid="gameover-back-button"
            onClick={handleMenu}
            className="px-5 py-3 border-2 font-bold uppercase tracking-widest transition-all duration-200 flex items-center gap-2"
            style={btnStyle}
            onMouseEnter={btnHoverIn}
            onMouseLeave={btnHoverOut}
          >
            <ArrowLeft size={16} />
            MENU
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameOverOverlay;
